import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Gauge, Route as RouteIcon, Clock, Pause, TrendingUp, ListOrdered, Activity, AlertTriangle, ChevronDown, ChevronUp, FileText, FileSpreadsheet, Info, Map as MapIcon, LayoutDashboard, CalendarDays, X } from "lucide-react";
import CaltracRoadMap from "@/components/CaltracRoadMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NC_OFFSET = "+11:00"; // Nouvelle-Calédonie, fuseau fixe (pas d'heure d'été)
const PRESETS = [["today", "Aujourd'hui", 24], ["yesterday", "Hier", 48], ["7j", "7 jours", 168], ["14j", "14 jours", 336]];
const MAX_DAYS = 14;

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fmtDur = (min) => { const m = Math.max(0, Math.round(min || 0)); const h = Math.floor(m / 60); return h > 0 ? `${h} h ${String(m % 60).padStart(2, "0")}` : `${m}`; };

const Card = ({ icon: Icon, label, value, unit, tone = "teal", testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone === "red" ? "bg-[#EF4444]/15 text-[#EF4444]" : tone === "orange" ? "bg-[#F5821F]/15 text-[#F5821F]" : "bg-[#17A2B8]/15 text-[#17A2B8]"}`}><Icon className="w-5 h-5" /></div>
    <div className="min-w-0"><p className="text-[11px] text-muted-foreground truncate">{label}</p><p className="text-lg font-bold leading-tight">{value}<span className="text-xs font-normal text-muted-foreground ml-1">{unit}</span></p></div>
  </div>
);

export default function SpeedDrivingPage() {
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fCompany, setFCompany] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  const [preset, setPreset] = useState("7j");
  const [reference, setReference] = useState("vehicle");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [bandFilter, setBandFilter] = useState(null);
  const [view, setView] = useState("dashboard");
  const reqSeq = useRef(0);        // garde anti-réponse-périmée
  const abortRef = useRef(null);   // annulation de la requête précédente

  // Validation période personnalisée (fuseau NC) — borne 14 jours identique au backend.
  const customSpanDays = (customStart && customEnd) ? ((Date.parse(`${customEnd}T00:00:00${NC_OFFSET}`) - Date.parse(`${customStart}T00:00:00${NC_OFFSET}`)) / 86400000) + 1 : null;
  const customInvalid = preset === "custom" && (!customStart || !customEnd
    ? "Choisissez une date de début et de fin."
    : (customSpanDays <= 0 ? "La date de fin doit être postérieure ou égale à la date de début."
      : (customSpanDays > MAX_DAYS ? `Période limitée à ${MAX_DAYS} jours maximum.` : null)));

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => setCompanies([]));
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => setVehicles([]));
  }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (preset === "custom" && customStart && customEnd) {
      p.set("start", `${customStart}T00:00:00${NC_OFFSET}`);
      p.set("end", `${customEnd}T23:59:59${NC_OFFSET}`);
    } else {
      p.set("hours", String((PRESETS.find((x) => x[0] === preset) || [])[2] || 168));
    }
    if (fCompany) p.set("company_id", fCompany);
    if (fVehicle) p.set("vehicle_id", fVehicle);
    p.set("reference", reference);
    return p.toString();
  }, [preset, customStart, customEnd, fCompany, fVehicle, reference]);

  const load = useCallback(() => {
    // Période perso incomplète/invalide : on NE lance PAS de requête et on NE détruit PAS
    // les derniers résultats valides. Un indice neutre (customInvalid) est affiché, pas une erreur.
    if (preset === "custom" && customInvalid) {
      if (abortRef.current) abortRef.current.abort();
      reqSeq.current += 1; // invalide toute requête encore en vol
      setLoading(false);
      return;
    }
    const mySeq = (reqSeq.current += 1);
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(null);
    api.get(`/reports/speed-driving?${qs()}`, { signal: ctrl.signal })
      .then((r) => {
        if (mySeq !== reqSeq.current) return; // réponse périmée : ignorée
        setData(r.data); setBandFilter(null); setError(null);
      })
      .catch((e) => {
        if (mySeq !== reqSeq.current) return; // réponse périmée : ignorée
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return; // requête annulée (nouvelle sélection)
        // On conserve les derniers résultats valides : une erreur/chargement ne devient JAMAIS 0 / « aucun épisode ».
        setError(e?.response?.data?.detail || "Chargement impossible pour cette période. Réessayez.");
      })
      .finally(() => { if (mySeq === reqSeq.current) setLoading(false); });
  }, [qs, preset, customInvalid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const s = data?.summary;
  const episodes = (data?.episodes || []).filter((e) => bandFilter == null || e.band_key === bandFilter);

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="speed-driving-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Gauge className="w-6 h-6 brand-teal" />
            <h1 className="text-xl lg:text-2xl font-bold">Vitesse et conduite</h1>
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-1" data-testid="sd-view-toggle">
            <button onClick={() => setView("dashboard")} data-testid="sd-view-dashboard"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 transition-colors ${view === "dashboard" ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <LayoutDashboard className="w-4 h-4" /> Tableau de bord
            </button>
            <button onClick={() => setView("map")} data-testid="sd-view-map"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 transition-colors ${view === "map" ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <MapIcon className="w-4 h-4" /> Carte routière
            </button>
          </div>
        </div>

        {/* Avertissement honnêteté limite routière */}
        {view === "dashboard" && (
        <div className="flex items-start gap-2 rounded-lg border border-[#F5821F]/30 bg-[#F5821F]/10 p-3 text-xs" data-testid="speed-driving-disclaimer">
          <Info className="w-4 h-4 brand-orange mt-0.5 shrink-0" />
          <p>{data?.disclaimer || "Les tranches sont calculées sur le seuil configuré de chaque véhicule, pas sur une limitation routière. La limite par tronçon est indiquée « indisponible »."}</p>
        </div>
        )}

        {/* Source de la vitesse (CAN préférée, repli GPS étiqueté) */}
        {view === "dashboard" && data?.speed_source && (
          <div className="flex items-start gap-2 rounded-lg border border-[#17A2B8]/30 bg-[#17A2B8]/10 p-3 text-xs" data-testid="sd-speed-source">
            <Gauge className="w-4 h-4 brand-teal mt-0.5 shrink-0" />
            {data.speed_source.can_pct === 0 ? (
              <p>
                <span className="font-semibold">Aucune donnée CAN sur la période</span> — vitesse issue du GPS,{" "}
                <span className="font-bold">moins fiable</span> (couverture CAN{" "}
                <span className="data font-bold" data-testid="sd-can-pct">0%</span>,{" "}
                {data.speed_source.gps_fallback_measures} mesures GPS). {data.speed_source.note}
              </p>
            ) : (
              <p>
                <span className="font-semibold">Source vitesse : bus CAN préférée</span> — couverture CAN{" "}
                <span className="data font-bold" data-testid="sd-can-pct">{data.speed_source.can_pct}%</span>{" "}
                ({data.speed_source.can_measures} mesures CAN / {data.speed_source.gps_fallback_measures} repli GPS). {data.speed_source.note}
              </p>
            )}
          </div>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap items-end gap-3">
          {view === "dashboard" && (
            <label className="text-xs">Référence des excès
              <select data-testid="sd-reference" value={reference} onChange={(e) => setReference(e.target.value)} className="block mt-1 bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-sm">
                <option value="road">Limitation routière (VMA)</option>
                <option value="vehicle">Seuil véhicule (historique)</option>
              </select>
            </label>
          )}
          {companies.length > 1 && (
            <label className="text-xs">Société
              <select data-testid="sd-filter-company" value={fCompany} onChange={(e) => setFCompany(e.target.value)} className="block mt-1 bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-sm">
                <option value="">Toutes</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <label className="text-xs">Véhicule
            <select data-testid="sd-filter-vehicle" value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} className="block mt-1 bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-sm max-w-[200px]">
              <option value="">Tous</option>
              {vehicles.filter((v) => !fCompany || v.company_id === fCompany).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <div className="flex gap-1 items-center flex-wrap" data-testid="sd-presets">
            {PRESETS.map(([k, lab]) => (
              <button key={k} onClick={() => setPreset(k)} data-testid={`sd-preset-${k}`} className={`text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors ${preset === k ? "bg-[#17A2B8] text-white" : "border border-border hover:border-[#17A2B8]"}`}>{lab}</button>
            ))}
            <button onClick={() => setPreset("custom")} data-testid="sd-preset-custom" className={`inline-flex items-center gap-1 text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors ${preset === "custom" ? "bg-[#17A2B8] text-white" : "border border-border hover:border-[#17A2B8]"}`}><CalendarDays className="w-3.5 h-3.5" /> Période perso</button>
            {preset === "custom" && (
              <span className="inline-flex items-center gap-1.5 ml-1">
                <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)} data-testid="sd-custom-start" className="bg-secondary/40 border border-border rounded-md px-2 py-1 text-xs" />
                <span className="text-muted-foreground text-xs">→</span>
                <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => setCustomEnd(e.target.value)} data-testid="sd-custom-end" className="bg-secondary/40 border border-border rounded-md px-2 py-1 text-xs" />
                <span className="text-[10px] text-muted-foreground">(NC)</span>
              </span>
            )}
          </div>
          <div className="ml-auto flex gap-2">
            {view === "map" && !fVehicle && (
              <span className="text-[11px] text-muted-foreground self-center italic" data-testid="sd-map-vehicle-hint">Choisissez un véhicule pour la carte</span>
            )}
            {view === "dashboard" && (<>
            {bandFilter != null && (
              <span className="self-center text-[10px] text-amber-600 font-semibold italic" data-testid="sd-export-allbands-note">Exports = toutes tranches</span>
            )}
            <a href={`${BACKEND_URL}/api/reports/speed-driving/export.pdf?${qs()}`} target="_blank" rel="noreferrer" title="Exporte toutes les tranches de la période (le filtre d'affichage par tranche n'est pas appliqué à l'export)" data-testid="sd-export-pdf" className="inline-flex items-center gap-1.5 rounded-md bg-[#F5821F] hover:bg-[#e0741a] text-white text-xs font-bold px-3 py-2"><FileText className="w-4 h-4" /> PDF (toutes tranches)</a>
            <a href={`${BACKEND_URL}/api/reports/speed-driving/export.xlsx?${qs()}`} target="_blank" rel="noreferrer" title="Exporte toutes les tranches de la période (le filtre d'affichage par tranche n'est pas appliqué à l'export)" data-testid="sd-export-xlsx" className="inline-flex items-center gap-1.5 rounded-md border border-[#17A2B8] text-[#17A2B8] hover:bg-[#17A2B8]/10 text-xs font-bold px-3 py-2"><FileSpreadsheet className="w-4 h-4" /> Excel (toutes tranches)</a>
            </>)}
          </div>
        </div>

        {view === "map" && (
          <CaltracRoadMap vehicleId={fVehicle} qs={qs()} />
        )}

        {/* Indice NEUTRE (pas une erreur) quand la période perso est incomplète/invalide : ne détruit pas les résultats affichés */}
        {view === "dashboard" && preset === "custom" && customInvalid && (
          <div className="flex items-start gap-2 rounded-lg border border-[#17A2B8]/30 bg-[#17A2B8]/10 p-3 text-sm" data-testid="sd-period-hint">
            <CalendarDays className="w-4 h-4 brand-teal mt-0.5 shrink-0" />
            <p>{customInvalid}</p>
          </div>
        )}

        {/* Premier chargement (aucun résultat encore disponible) */}
        {loading && view === "dashboard" && !s && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-busy="true" data-testid="sd-loading">
            <span className="inline-block w-4 h-4 rounded-full border-2 border-[#17A2B8] border-t-transparent animate-spin" />
            Chargement…
          </div>
        )}

        {/* Erreur réelle : explicite + Réessayer. Les derniers résultats valides restent affichés en dessous. */}
        {error && view === "dashboard" && !loading && (
          <div className="flex items-start gap-3 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm" data-testid="sd-error" role="alert">
            <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p>{error}</p>
              {s && <p className="text-xs text-muted-foreground mt-1">Les résultats affichés ci-dessous correspondent à la dernière période chargée avec succès.</p>}
            </div>
            <button onClick={load} data-testid="sd-retry" className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-[#EF4444] hover:bg-[#dc2626] text-white text-xs font-bold px-3 py-1.5">Réessayer</button>
          </div>
        )}

        {view === "dashboard" && s && (
          <div className={`relative space-y-5 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`} aria-busy={loading} data-testid="sd-dashboard-body">
            {loading && (
              <div className="absolute -top-2 right-0 z-10 inline-flex items-center gap-2 rounded-full bg-[#17A2B8] text-white text-[11px] font-semibold px-3 py-1 shadow" role="status" aria-live="polite" data-testid="sd-updating">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Mise à jour…
              </div>
            )}
            {/* Contexte : période visible (NC) + référence explicite (seuil véhicule OU VMA + marge) */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="sd-context">
              <span data-testid="sd-period"><span className="text-muted-foreground">Période :</span> <span className="font-semibold">{fmtDay(data.start)} → {fmtDay(data.end)}</span> <span className="text-muted-foreground">(NC)</span></span>
              {data.reference === "road" ? (
                <>
                  <span data-testid="sd-reference-label"><span className="text-muted-foreground">Référence :</span> <span className="font-semibold">Limitation routière (VMA officielle, BDROUTE-NC/DITTT)</span></span>
                  <span data-testid="sd-margin"><span className="text-muted-foreground">Marge effective :</span> <span className="font-semibold">{data.margin_effective ? (data.margin_effective.mode === "pct" ? `${data.margin_effective.value} %` : `${data.margin_effective.value} km/h`) : "variable selon véhicule"}</span></span>
                  <span data-testid="sd-coverage" className="text-muted-foreground">Couverture : {data.coverage?.known_limit ?? 0} pts à limite fiable / {data.coverage?.points ?? 0} analysés ({data.coverage?.unknown ?? 0} inconnus, {data.coverage?.ambiguous ?? 0} ambigus)</span>
                </>
              ) : (
                <>
                  <span data-testid="sd-threshold"><span className="text-muted-foreground">Seuil {data.threshold_mode === "manual" ? "manuel" : "configuré"} :</span>{" "}
                    <span className="font-semibold">{data.threshold_kmh != null ? `${data.threshold_kmh} km/h` : (data.threshold_values?.length ? `${Math.min(...data.threshold_values)}–${Math.max(...data.threshold_values)} km/h selon véhicule` : "—")}</span></span>
                  <span className="text-muted-foreground">Limite routière : <span className="italic">indisponible</span></span>
                </>
              )}
              <span className="text-muted-foreground">Contrôle absolu : &gt; {data.abs_threshold_kmh} km/h</span>
            </div>

            {/* Distinction explicite : aucune infraction vs données absentes */}
            {s.episodes_count === 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-xs" data-testid="sd-empty-explain">
                <Info className="w-4 h-4 brand-teal mt-0.5 shrink-0" />
                {s.measured_points_total > 0 ? (
                  <p data-testid="sd-no-infraction"><span className="font-semibold">Aucun {data.reference === "road" ? "excès au-dessus de la VMA" : "dépassement du seuil configuré"}</span> sur la période — {s.measured_points_total.toLocaleString("fr-FR")} mesures analysées{data.reference === "road" ? ` (${data.coverage?.known_limit ?? 0} à limite fiable)` : `, vitesse max ${s.max_speed_kmh} km/h`}. Les données existent ; il n'y a simplement aucune infraction selon la référence choisie. {data.reference === "road" ? "Les tronçons sans limite fiable (VS supposée/inconnue/ambiguë) sont non actionnables." : "La limitation routière (VMA) est disponible via la référence « Limitation routière »."}</p>
                ) : (
                  <p data-testid="sd-no-data"><span className="font-semibold">Aucune donnée de position</span> pour ce périmètre et cette période. Vérifiez le véhicule et les dates.</p>
                )}
              </div>
            )}

            {/* Cartes synthèse — activité (mode seuil véhicule) ou couverture (mode VMA) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sd-cards">
              {data.reference === "road" ? (
                <Card icon={RouteIcon} label="Points à limite fiable" value={`${data.coverage?.known_limit ?? 0} / ${data.coverage?.points ?? 0}`} unit="" testid="sd-card-coverage" />
              ) : (<>
                <Card icon={RouteIcon} label="Distance" value={s.distance_km} unit="km" testid="sd-card-distance" />
                <Card icon={Clock} label="Conduite" value={fmtDur(s.driving_min)} unit="min" testid="sd-card-driving" />
                <Card icon={Pause} label="Ralenti" value={fmtDur(s.idle_min)} unit="min" testid="sd-card-idle" />
              </>)}
              <Card icon={TrendingUp} label="Vitesse maximale" value={s.max_speed_kmh} unit="km/h" testid="sd-card-maxspeed" />
              <Card icon={ListOrdered} label="Nombre d'épisodes" value={s.episodes_count} unit="" testid="sd-card-episodes" />
              <Card icon={Activity} label={data.reference === "road" ? "Mesures > VMA + marge" : "Mesures > seuil"} value={s.measures_count} unit="" testid="sd-card-measures" />
              <Card icon={AlertTriangle} label={`Vitesse > ${data.abs_threshold_kmh} km/h`} value={s.abs_over_145_count} unit="" tone="red" testid="sd-card-abs" />
            </div>

            {/* Tranches cliquables */}
            <div>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="text-xs font-semibold text-muted-foreground">{data.reference === "road" ? "Tranches d'excès au-dessus de la VMA (cliquer pour filtrer le détail)" : "Tranches de dépassement du seuil configuré (cliquer pour filtrer le détail)"}</p>
                {bandFilter != null && (
                  <button onClick={() => setBandFilter(null)} data-testid="sd-filter-chip" className="inline-flex items-center gap-1 rounded-full bg-[#17A2B8]/15 text-[#17A2B8] text-[11px] font-semibold px-2.5 py-1">
                    Filtre actif : {s.bands.find((b) => b.key === bandFilter)?.label || bandFilter} <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="sd-bands">
                {s.bands.map((b) => (
                  <button key={b.key} data-testid={`sd-band-${b.key}`} aria-pressed={bandFilter === b.key} onClick={() => { setBandFilter(bandFilter === b.key ? null : b.key); setShowDetail(true); }}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${bandFilter === b.key ? "border-[#17A2B8] ring-2 ring-[#17A2B8]/40" : "border-border hover:border-[#17A2B8]"}`}>
                    <span className="w-3 h-3 rounded-sm" style={{ background: b.color }} />
                    <span>{b.label}</span>
                    <span className="data font-bold">{b.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Détail masqué par défaut */}
            <div>
              <button onClick={() => setShowDetail((v) => !v)} data-testid="sd-toggle-detail" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#17A2B8]">
                {showDetail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showDetail ? "Masquer le détail" : "Voir le détail"} ({episodes.length})
              </button>
              {showDetail && (
                <div className="mt-3 overflow-x-auto rounded-xl border border-border" data-testid="sd-detail-table">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary/60 text-muted-foreground">
                      <tr>{["Date/heure", "Véhicule", "Vitesse", "Source", "Limite tronçon", "Delta", "Tranche", ">145", "Adresse", "Maps", "Conducteur"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {episodes.length === 0 && <tr><td colSpan={11} className="text-center text-muted-foreground py-6">Aucun épisode.</td></tr>}
                      {episodes.map((e, i) => (
                        <tr key={i} data-testid={`sd-row-${i}`} className="border-t border-border/60">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDT(e.start_at)}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{e.vehicle} <span className="text-muted-foreground">{e.plate}</span></td>
                          <td className="px-3 py-1.5 data font-bold">{e.max_speed_kmh} km/h</td>
                          <td className="px-3 py-1.5 whitespace-nowrap" data-testid={`sd-row-source-${i}`}>
                            <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${e.speed_source === "can" ? "bg-[#17A2B8]/15 text-[#17A2B8]" : "bg-[#F5821F]/15 text-[#F5821F]"}`}>{e.speed_source_label || "GPS, moins fiable"}</span>
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground italic">{data.reference === "road" && e.speed_limit_kmh != null ? `${e.speed_limit_kmh} km/h` : "indisponible"}</td>
                          <td className="px-3 py-1.5 data">+{e.max_excess_kmh}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{e.band_label}</td>
                          <td className="px-3 py-1.5">{e.abs_over_145 ? <span className="text-[#EF4444] font-bold">oui</span> : ""}</td>
                          <td className="px-3 py-1.5 max-w-[220px] truncate">{e.location || "—"}</td>
                          <td className="px-3 py-1.5">{e.lat != null ? <a className="text-[#17A2B8] underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${e.lat},${e.lon}`}>Carte</a> : "—"}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
